'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useDarkMode } from '@/components/DarkModeProvider';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabaseClient';
import { TemplateCardSkeleton, CategoryFilterSkeleton } from '@/components/ui/SkeletonLoader';
import ErrorBoundary from '@/components/ErrorBoundary';
import { trackPlatformFilter, trackSearchQuery } from '@/lib/analytics';
import SearchAutocomplete from '@/components/SearchAutocomplete';

interface Template {
  id: string;
  name: string;
  platform: string;
  category: string | null;
  price: number;
  rating: number;
  reviews_count: number;
  downloads_count: number;
  thumbnail_url: string | null;
  description: string | null;
  tags: string[];
  created_at: string;
}

interface TemplatesClientProps {
  initialTemplates?: Template[];
}

const PLATFORMS = ['All', 'shopify', 'wordpress', 'woocommerce', 'html'];
const CATEGORIES = ['All', 'E-Commerce', 'Blog', 'Agency', 'Portfolio', 'Landing Page', 'SaaS', 'Restaurant', 'Corporate', 'Fitness', 'Beauty'];
const SORT_OPTIONS = [
{ value: 'popular', label: 'Most Popular' },
{ value: 'newest', label: 'Newest First' },
{ value: 'price-asc', label: 'Price: Low to High' },
{ value: 'price-desc', label: 'Price: High to Low' },
{ value: 'rating', label: 'Top Rated' }];

const ITEMS_PER_PAGE = 12;

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }
  return false;
}

function StarRating({ rating, reviews }: {rating: number;reviews: number;}) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) =>
      <Icon
        key={s}
        name="StarIcon"
        size={11}
        variant={s <= Math.floor(rating) ? 'solid' : 'outline'}
        className={s <= Math.floor(rating) ? 'star-filled' : 'star-empty'} />

      )}
      <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>
        {rating} ({reviews})
      </span>
    </div>);

}

export default function TemplatesClient({ initialTemplates = [] }: TemplatesClientProps) {
  return (
    <ErrorBoundary>
      <TemplatesContent initialTemplates={initialTemplates} />
    </ErrorBoundary>
  );
}

function TemplatesContent({ initialTemplates = [] }: TemplatesClientProps) {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [templates, setTemplates] = useState<Template[]>(initialTemplates);
  const [loading, setLoading] = useState(initialTemplates.length === 0);
  const [error, setError] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('popular');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 150]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnSale, setShowOnSale] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Only fetch client-side if no server-side data was provided
    if (initialTemplates.length === 0) {
      loadTemplates();
    }
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .eq('is_published', true)
        .order('downloads_count', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          setError('Failed to load templates');
          return;
        }
      }

      setTemplates(data || []);
    } catch (err: any) {
      console.error('Error loading templates:', err.message);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformChange = (platform: string) => {
    setSelectedPlatform(platform);
    setCurrentPage(1);
    if (platform !== 'All') {
      trackPlatformFilter(platform);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
    // Debounce search tracking
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        const results = templates.filter(
          (t) =>
            t.name.toLowerCase().includes(query.toLowerCase()) ||
            (t.category || '').toLowerCase().includes(query.toLowerCase())
        ).length;
        trackSearchQuery(query.trim(), results);
      }, 800);
    }
  };

  const filteredTemplates = templates.filter((t) => {
    if (selectedPlatform !== 'All' && t.platform !== selectedPlatform) return false;
    if (selectedCategory !== 'All' && t.category !== selectedCategory) return false;
    if (t.price < priceRange[0] || t.price > priceRange[1]) return false;
    if (showNew) {
      const createdDate = new Date(t.created_at);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      if (createdDate < thirtyDaysAgo) return false;
    }
    if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase()) && !(t.category || '').toLowerCase().includes(searchQuery.toLowerCase()) && !(t.tags || []).some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'price-asc') return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
    if (sortBy === 'newest') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    return (b.downloads_count || 0) - (a.downloads_count || 0);
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / ITEMS_PER_PAGE));
  const paginatedTemplates = filteredTemplates.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPlatform, selectedCategory, sortBy, priceRange, showNew]);

  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('visible');}),
      { threshold: 0.05 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [filteredTemplates]);

  const FilterSidebar = () =>
  <aside
    className="w-full lg:w-64 xl:w-72 flex-shrink-0"
    style={{ color: 'var(--text-primary)' }}>
    
      <div
      className="rounded-2xl p-5 sticky top-20"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>
            Filters
          </h3>
          <button
          onClick={() => {
            setSelectedPlatform('All');
            setSelectedCategory('All');
            setPriceRange([0, 150]);
            setShowOnSale(false);
            setShowNew(false);
            setSearchQuery('');
          }}
          className="text-xs font-medium"
          style={{ color: 'var(--accent)' }}>
          
            Clear all
          </button>
        </div>

        {/* Platform */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
            Platform
          </p>
          {loading ? (
            <CategoryFilterSkeleton />
          ) : (
            <div className="flex flex-col gap-1.5">
              {PLATFORMS.map((p) =>
            <button
              key={p}
              onClick={() => handlePlatformChange(p)}
              className="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-left transition-all"
              style={{
                background: selectedPlatform === p ? 'var(--accent-light)' : 'transparent',
                color: selectedPlatform === p ? 'var(--accent)' : 'var(--text-secondary)',
                fontWeight: selectedPlatform === p ? 600 : 400
              }}>
              
                  {p === 'All' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
                  {selectedPlatform === p && <Icon name="CheckIcon" size={14} />}
                </button>
            )}
            </div>
          )}
        </div>

        {/* Category */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
            Category
          </p>
          {loading ? (
            <CategoryFilterSkeleton />
          ) : (
            <div className="flex flex-col gap-1.5">
              {CATEGORIES.map((c) =>
            <button
              key={c}
              onClick={() => setSelectedCategory(c)}
              className="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-left transition-all"
              style={{
                background: selectedCategory === c ? 'var(--accent-light)' : 'transparent',
                color: selectedCategory === c ? 'var(--accent)' : 'var(--text-secondary)',
                fontWeight: selectedCategory === c ? 600 : 400
              }}>
              
                  {c}
                  {selectedCategory === c && <Icon name="CheckIcon" size={14} />}
                </button>

            )}
            </div>
          )}
        </div>

        {/* Price */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
            Max Price: ${priceRange[1]}
          </p>
          <input
          type="range"
          min={0}
          max={150}
          value={priceRange[1]}
          onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
          className="range-slider w-full" />
        
          <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            <span>$0</span>
            <span>$150</span>
          </div>
        </div>

        {/* Toggles */}
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
            type="checkbox"
            checked={showNew}
            onChange={(e) => setShowNew(e.target.checked)}
            className="custom-checkbox" />
          
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>New Arrivals</span>
          </label>
        </div>
      </div>
    </aside>;


  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main className="flex-1 pt-16">
        {/* Page header */}
        <div
          className="py-12 border-b"
          style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}>
          
          <div className="max-w-8xl mx-auto px-6">
            <h1
              className="font-display font-light text-4xl lg:text-5xl mb-2"
              style={{ color: 'var(--text-primary)' }}>
              
              Browse{' '}
              <span className="italic" style={{ color: 'var(--accent)' }}>
                Templates
              </span>
            </h1>
            <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
              {loading ? (
                <span className="inline-block h-5 w-48 rounded animate-pulse" style={{ background: 'var(--bg-card)' }} />
              ) : (
                `${filteredTemplates.length} premium templates across multiple platforms`
              )}
            </p>
          </div>
        </div>

        <div className="max-w-8xl mx-auto px-6 py-8">
          {error && (
            <div
              className="flex items-center gap-2.5 p-3 rounded-xl text-sm mb-6"
              style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}
            >
              <Icon name="ExclamationCircleIcon" size={16} />
              {error}
            </div>
          )}

          {/* Search + controls bar */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <SearchAutocomplete
              value={searchQuery}
              onChange={handleSearchChange}
              templates={templates}
              placeholder="Search templates..."
              className="flex-1 min-w-[200px] max-w-sm"
            />

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium"
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)'
              }}>
              {SORT_OPTIONS.map((opt) =>
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              )}
            </select>

            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2.5 rounded-xl transition-all ${viewMode === 'grid' ? 'shadow-sm' : ''}`}
                style={{
                  background: viewMode === 'grid' ? 'var(--accent)' : 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  color: viewMode === 'grid' ? '#FFFFFF' : 'var(--text-muted)'
                }}
                aria-label="Grid view">
                <Icon name="Squares2X2Icon" size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2.5 rounded-xl transition-all ${viewMode === 'list' ? 'shadow-sm' : ''}`}
                style={{
                  background: viewMode === 'list' ? 'var(--accent)' : 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  color: viewMode === 'list' ? '#FFFFFF' : 'var(--text-muted)'
                }}
                aria-label="List view">
                <Icon name="ListBulletIcon" size={16} />
              </button>
            </div>

            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden btn-secondary">
              <Icon name="AdjustmentsHorizontalIcon" size={16} />
              Filters
            </button>
          </div>

          <div className="flex gap-8">
            <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block`}>
              <FilterSidebar />
            </div>

            <div className="flex-1" ref={sectionRef}>
              {loading ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <TemplateCardSkeleton key={i} />
                  ))}
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-16">
                  <Icon name="MagnifyingGlassIcon" size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <h3 className="font-display font-medium text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                    No templates found
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Try adjusting your filters or search query
                  </p>
                </div>
              ) : (
                <>
                  <div className={viewMode === 'grid' ? 'grid sm:grid-cols-2 xl:grid-cols-3 gap-6' : 'flex flex-col gap-4'}>
                    {paginatedTemplates.map((t) => (
                      <Link
                        key={t.id}
                        href={`/template-detail?id=${t.id}`}
                        className="reveal template-card group"
                        style={{
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border)',
                          borderRadius: '1rem',
                          overflow: 'hidden',
                          display: viewMode === 'list' ? 'flex' : 'block',
                          gap: viewMode === 'list' ? '1rem' : '0'
                        }}>
                        <div className={viewMode === 'list' ? 'w-48 flex-shrink-0' : 'relative aspect-video'}>
                          <AppImage
                            src={t.thumbnail_url || '/assets/images/no_image.png'}
                            alt={`${t.name} template preview`}
                            fill
                            className="object-cover" />
                          {t.is_featured && (
                            <span
                              className="absolute top-2 left-2 px-2 py-1 rounded-lg text-2xs font-semibold"
                              style={{ background: 'var(--accent)', color: '#FFFFFF' }}>
                              Featured
                            </span>
                          )}
                        </div>
                        <div className="p-4 flex-1">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-display font-medium text-base" style={{ color: 'var(--text-primary)' }}>
                              {t.name}
                            </h3>
                            <span
                              className="px-2 py-0.5 rounded text-2xs font-semibold flex-shrink-0"
                              style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
                              {t.platform.charAt(0).toUpperCase() + t.platform.slice(1)}
                            </span>
                          </div>
                          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                            {t.category || 'Uncategorized'}
                          </p>
                          <StarRating rating={t.rating || 0} reviews={t.reviews_count || 0} />
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-baseline gap-2">
                              <span className="font-display font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                                ${t.price}
                              </span>
                            </div>
                            <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>
                              {t.downloads_count || 0} downloads
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-10">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all"
                        style={{
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border)',
                          color: currentPage === 1 ? 'var(--text-muted)' : 'var(--text-primary)',
                          opacity: currentPage === 1 ? 0.5 : 1,
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <Icon name="ChevronLeftIcon" size={14} />
                        Previous
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter((page) => {
                            if (totalPages <= 7) return true;
                            if (page === 1 || page === totalPages) return true;
                            if (Math.abs(page - currentPage) <= 2) return true;
                            return false;
                          })
                          .reduce((acc: (number | string)[], page, idx, arr) => {
                            if (idx > 0 && typeof arr[idx - 1] === 'number' && (page as number) - (arr[idx - 1] as number) > 1) {
                              acc.push('...');
                            }
                            acc.push(page);
                            return acc;
                          }, [])
                          .map((item, idx) =>
                            item === '...' ? (
                              <span
                                key={`ellipsis-${idx}`}
                                className="px-2 py-2 text-sm"
                                style={{ color: 'var(--text-muted)' }}
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item}
                                onClick={() => setCurrentPage(item as number)}
                                className="w-9 h-9 rounded-xl text-sm font-medium transition-all"
                                style={{
                                  background: currentPage === item ? 'var(--accent)' : 'var(--bg-card)',
                                  border: '1px solid var(--border)',
                                  color: currentPage === item ? '#FFFFFF' : 'var(--text-primary)',
                                }}
                              >
                                {item}
                              </button>
                            )
                          )}
                      </div>

                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all"
                        style={{
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border)',
                          color: currentPage === totalPages ? 'var(--text-muted)' : 'var(--text-primary)',
                          opacity: currentPage === totalPages ? 0.5 : 1,
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Next
                        <Icon name="ChevronRightIcon" size={14} />
                      </button>
                    </div>
                  )}

                  {/* Results info */}
                  <p className="text-center text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
                    Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredTemplates.length)} of {filteredTemplates.length} templates
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}